import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Space, Tooltip, Typography } from "antd";

import { updatePatientFacility } from "../../../api/api";
import { selectPatientDetails, selectProvidersFacility } from "../../../store/selector";
import ErrorMessage from "../../customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../../customComponent/customMessages/SuccessMessage";
import ACTIONS from "../../../store/action";
import { fontWeights, sizes } from "../../../constant/styles";
import { GRAY_400, TEXT4, WHITE } from "../../../constant/colors";
import FacilitiesSelectField from "../../Select/FacilitiesSelectField";
import { useGetSetting } from "../../../hooks/getSetting";

const { Text } = Typography;

const PatientFacility = ({
  selectStyle = {},
  secondaryTextStyle = { fontSize: sizes.small, color: TEXT4, fontWeight: fontWeights.semibold }
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isSelectHover, setIsSelectHover] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [patientFacilities, setPatientFacilities] = useState([]);
  const { patientId } = useParams();

  const facilities = useSelector(selectProvidersFacility);
  const patientDetails = useSelector(selectPatientDetails);
  const [rawBillableFacilityIds] = useGetSetting(["billable-facilities"]);
  const billableFacilityIds = Array.isArray(rawBillableFacilityIds) ? rawBillableFacilityIds : [];

  useEffect(() => {
    if (!facilities || !facilities.length) return;

    if (!patientDetails?.facilities?.length) {
      setPatientFacilities([]);
      return;
    }

    const next = patientDetails.facilities
      .map((facility) => {
        const chosen = facilities.find(({ id }) => id && id === facility.id);
        if (chosen) return chosen;
      })
      .filter((item) => item)
      .sort((a, b) => a.name.localeCompare(b.name));

    setPatientFacilities(next);
  }, [facilities, patientDetails]);

  const updateFacilityHandler = async (values) => {
    try {
      setIsFetching(true);
      const newFacilities = values
        .map((value) => facilities.find((facility) => facility.id === value))
        .filter((f) => f);

      const updateFacilityRes = await updatePatientFacility(patientId, values);

      if (updateFacilityRes && updateFacilityRes.data && updateFacilityRes.data.success) {
        SuccessMessage(t("successMessages.successfully_updated_facility"));
        setPatientFacilities(newFacilities);

        const updatedPatientDetails = {
          ...patientDetails,
          facilities: newFacilities.map((f) => ({ id: f.id, name: f.name })),
          facility: updateFacilityRes.data.facility,
          facilityId: updateFacilityRes.data.facilityId
        };

        dispatch(ACTIONS.updatePatientDetailsAction(updatedPatientDetails));
      } else {
        ErrorMessage(t("errorMessages.failed_to_update_facility"));
      }
    } catch (error) {
      ErrorMessage(t("errorMessages.failed_to_update_facility"));
    } finally {
      setIsFetching(false);
    }
  };

  const facilitiesIds = patientFacilities?.map(({ id }) => id) || [];
  const facilitiesTextToDisplay = patientFacilities?.map((facility) => facility.name).join(" | ") || "";

  return (
    <Space direction="vertical" size={0} style={{ width: "100%" }}>
      <Text style={{ ...secondaryTextStyle, paddingLeft: 12 }}>{t("patientTopSection.facilities")}</Text>
      <Tooltip
        open={!!facilitiesTextToDisplay.length && !isSelectOpen && isSelectHover}
        title={<Text>{facilitiesTextToDisplay}</Text>}
        color={WHITE}
      >
        <FacilitiesSelectField
          id="patient_header_facilities"
          facilities={facilities}
          value={facilitiesIds}
          onChange={updateFacilityHandler}
          loading={isFetching}
          bordered={false}
          showArrow={isSelectHover || isFetching}
          maxTagTextLength={patientFacilities.length > 1 ? 8 : 16}
          placeholder={<Text style={{ color: GRAY_400, fontWeight: 400 }}>{t("no_facilities")}</Text>}
          style={selectStyle}
          onMouseEnter={() => setIsSelectHover(true)}
          onMouseLeave={() => setIsSelectHover(false)}
          onDropdownVisibleChange={(open) => setIsSelectOpen(open)}
          getPopupContainer={(trigger) => trigger.parentNode}
          nonRemovableFacilityIds={billableFacilityIds}
        />
      </Tooltip>
    </Space>
  );
};

export { PatientFacility };
